import { useQuery } from '@tanstack/react-query';
import { useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';

import type { StorefrontCategory } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import {
  useCategoryPath,
  useShelfCategoryTree,
  type ShelfBranch,
} from '@/features/storefront/hooks/useShelfCategories';
import { CHROME_PANEL_STYLE } from '@/features/storefront/layouts/chrome/types';
import { useStorefrontPaths, type StorefrontPaths } from '@/features/storefront/paths';
import { safeHref } from '@/features/storefront/sections/schema';
import type { MegaMenuStyle, StorefrontTheme } from '@/features/storefront/themeSchema';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';

/**
 * The mega menu: a department that opens a panel the width of the page, its
 * sub-departments in columns, and - when the merchant asks for one - a promo
 * tile beside them.
 *
 * Two kinds of header open one. Emeraude and Tumble open one from the
 * merchant's own menu, when an item's children have children; every family
 * opens one from the shop's own category strip when the merchant turns
 * `header.mega_menu` on. Both draw their columns with {@link MegaColumns} and
 * open and close by {@link useHoverDisclosure}, so the three panels behave as
 * one: hover or focus opens, Escape and a departing focus close.
 */

/** Whether the header's category navigation opens mega panels, and how. */
export function megaMenuStyle(header: StorefrontTheme['header'] | undefined): MegaMenuStyle {
  return header?.mega_menu ?? 'off';
}

/**
 * Open on hover and on focus, closed on Escape and when focus leaves.
 *
 * Spread `props` on the element that holds both the trigger and the panel:
 * the panel being inside it is what makes the pair one hover target.
 */
export function useHoverDisclosure() {
  const [open, setOpen] = useState(false);
  const props = {
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') setOpen(false);
    },
  };
  return { open, setOpen, props };
}

/** One column of a mega panel: its heading and the links under it. */
export interface MegaColumn {
  key: string;
  heading: ReactNode;
  links: ReactNode[];
}

/**
 * The panel's columns. The family brings its own voice through the class
 * names - Emeraude's serif, Tumble's rounded face - and the grid is shared.
 */
export function MegaColumns({
  columns,
  className,
  linksClassName,
}: {
  columns: MegaColumn[];
  className?: string;
  linksClassName?: string;
}) {
  return (
    <div className={cn('grid grid-cols-2 gap-x-10 gap-y-8 md:grid-cols-4', className)}>
      {columns.map((column) => (
        <div key={column.key} className="min-w-0">
          {column.heading}
          {column.links.length > 0 ? (
            <div className={cn('flex flex-col', linksClassName)}>{column.links}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * The promo tile: a picture, a heading and a link.
 *
 * The picture is the merchant's own when they chose one, the department's when
 * they did not, and a block of the theme's primary when neither exists - a
 * tile is never an empty frame. `to` is a router path; an off-site `href`
 * leaves the router alone, as a menu link does.
 */
function MegaPromoTile({
  heading,
  to,
  imageUrl,
  imageFocal,
}: {
  heading: string;
  to: { path: string } | { href: string };
  imageUrl: string | null;
  imageFocal?: StorefrontCategory['image_focal'];
}) {
  const body = (
    <>
      {imageUrl ? (
        <StorefrontImage
          url={imageUrl}
          sizes={fixedImageSizes(224)}
          alt=""
          className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-105"
          style={focalStyle(imageFocal)}
        />
      ) : (
        <div
          aria-hidden
          className="aspect-[4/3] w-full"
          style={{ backgroundColor: 'var(--storefront-palette-primary, var(--storefront-primary))' }}
        />
      )}
      <span className="mt-3 block text-base font-semibold group-hover:underline">{heading}</span>
    </>
  );
  const className = 'group hidden w-56 shrink-0 overflow-hidden md:block';
  return 'href' in to ? (
    <a href={to.href} className={className} rel="noreferrer" data-testid="mega-promo">
      {body}
    </a>
  ) : (
    <Link to={to.path} className={className} data-testid="mega-promo">
      {body}
    </Link>
  );
}

/** Where a tile or card leads: a router path, or an off-site address. */
export type MegaTarget = { path: string } | { href: string };

/**
 * A merchant's link as a {@link MegaTarget}, or `fallback` when they wrote
 * none. Off-site and `mailto:` links leave the router alone, as a menu link does.
 */
export function megaTarget(
  href: string | null | undefined,
  fallback: MegaTarget,
  paths: StorefrontPaths,
): MegaTarget {
  const safe = safeHref(href);
  if (!safe) return fallback;
  return /^(https?:)?\/\//.test(safe) || safe.startsWith('mailto:')
    ? { href: safe }
    : { path: paths.menu(safe) };
}

/** `path` with the listing's newest-first order asked for. */
export function newestFirst(path: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}sort=newest`;
}

const PANEL_TEXT = 'whitespace-normal text-start font-normal normal-case tracking-normal';

/**
 * A mega panel's frame, hung `fixed` from `top` (the bar's bottom edge).
 *
 * The column styles span the window, as they always have. The card style
 * keeps to the page column instead - the edges every section of the page
 * stands between - with a thin rule in the theme's primary along its top and
 * its lower corners rounded, so it reads as part of the page rather than a
 * sheet pulled over it. The strip either side of it lets the pointer through.
 */
export function MegaPanel({
  id,
  label,
  header,
  top,
  children,
}: {
  id: string;
  label: string;
  header: StorefrontTheme['header'] | undefined;
  top: number;
  children: ReactNode;
}) {
  if (megaMenuStyle(header) !== 'columns_with_cards') {
    return (
      <div
        id={id}
        role="region"
        aria-label={label}
        className={cn('fixed inset-x-0 z-50 max-h-[75vh] overflow-y-auto border-y shadow-xl', PANEL_TEXT)}
        style={{ ...CHROME_PANEL_STYLE, top }}
      >
        <StorefrontContainer className="flex items-start gap-10 py-8">{children}</StorefrontContainer>
      </div>
    );
  }
  return (
    <div
      id={id}
      role="region"
      aria-label={label}
      className={cn('pointer-events-none fixed inset-x-0 z-50', PANEL_TEXT)}
      style={{ top }}
    >
      <StorefrontContainer>
        <div
          data-mega-frame=""
          className="pointer-events-auto max-h-[75vh] overflow-y-auto rounded-b-2xl border-x border-t-2 border-b shadow-xl"
          style={{
            ...CHROME_PANEL_STYLE,
            borderTopColor: 'var(--storefront-palette-primary, var(--storefront-primary))',
          }}
        >
          <div className="flex items-start gap-10 p-8">{children}</div>
        </div>
      </StorefrontContainer>
    </div>
  );
}

/** One picture card, resolved: what it says, where it leads, what it shows. */
export interface MegaCardView {
  heading: string;
  to: MegaTarget;
  imageUrl: string | null;
  imageFocal?: StorefrontCategory['image_focal'];
}

type MegaCardDefault = Omit<MegaCardView, 'heading'>;

/** A picture a card may wear, and where its subject stands in it. */
export interface MegaPicture {
  imageUrl: string;
  imageFocal?: StorefrontCategory['image_focal'];
}

/**
 * The two cards' pictures, never a blank frame where the shop has any picture
 * at all: the panel's own first (its columns' departments), then the shop's
 * other departments, then its newest products. The products are only asked
 * for when the departments run short - a shop that pictured them pays nothing.
 */
export function useMegaPictures(preferred: ReadonlyArray<MegaPicture | null>, enabled: boolean): MegaPicture[] {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const shelves = useShelfCategoryTree({ limit: 50, enabled });
  const pictures: MegaPicture[] = [];
  const seen = new Set<string>();
  const add = (picture: MegaPicture | null | undefined) => {
    if (!picture?.imageUrl || seen.has(picture.imageUrl) || pictures.length >= 2) return;
    seen.add(picture.imageUrl);
    pictures.push(picture);
  };
  preferred.forEach(add);
  for (const { category, children } of shelves) {
    for (const each of [category, ...children]) {
      add(each.image_url ? { imageUrl: each.image_url, imageFocal: each.image_focal } : null);
    }
  }
  const short = enabled && pictures.length < 2;
  const products = useQuery({
    queryKey: [...scope, 'mega-pictures'],
    queryFn: () => source.products({ size: 6, sort: 'newest' }),
    enabled: short,
    staleTime: 5 * 60 * 1000,
  });
  for (const product of products.data?.items ?? []) {
    add(product.image_url ? { imageUrl: product.image_url } : null);
  }
  return pictures;
}

/**
 * The two cards of `columns_with_cards`: each the merchant's own choice,
 * field by field, over the panel's default for that position.
 */
export function useMegaCards(
  header: StorefrontTheme['header'] | undefined,
  defaults: [MegaCardDefault, MegaCardDefault],
): MegaCardView[] {
  const text = useShopContent();
  const paths = useStorefrontPaths();
  const headings = [text('theme.mega_card_best_selling'), text('theme.mega_card_new_arrivals')];
  return defaults.map((fallback, index) => {
    const own = header?.mega_menu_cards?.[index];
    return {
      heading: own?.heading?.trim() || headings[index],
      to: megaTarget(own?.href, fallback.to, paths),
      imageUrl: own?.image_url || fallback.imageUrl,
      imageFocal: own?.image_url ? null : fallback.imageFocal,
    };
  });
}

/**
 * One large picture card: a rounded picture with its caption centred under
 * it. A block of the theme's primary stands in for a missing picture.
 */
function MegaCard({ card }: { card: MegaCardView }) {
  const body = (
    <>
      <span className="block overflow-hidden rounded-2xl">
        {card.imageUrl ? (
          <StorefrontImage
            url={card.imageUrl}
            sizes={fixedImageSizes(320)}
            alt=""
            className="aspect-[4/5] w-full object-cover transition-transform duration-300 group-hover:scale-105"
            style={focalStyle(card.imageFocal)}
          />
        ) : (
          <span
            aria-hidden
            className="block aspect-[4/5] w-full"
            style={{ backgroundColor: 'var(--storefront-palette-primary, var(--storefront-primary))' }}
          />
        )}
      </span>
      <span className="mt-3 block text-center text-base group-hover:underline">{card.heading}</span>
    </>
  );
  const className = 'group block min-w-0';
  return 'href' in card.to ? (
    <a href={card.to.href} className={className} rel="noreferrer" data-testid="mega-card">
      {body}
    </a>
  ) : (
    <Link to={card.to.path} className={className} data-testid="mega-card">
      {body}
    </Link>
  );
}

/** The two cards side by side, beside the columns from the medium breakpoint. */
export function MegaCards({ cards }: { cards: MegaCardView[] }) {
  return (
    <div className="hidden w-[42%] max-w-2xl shrink-0 grid-cols-2 gap-5 md:grid">
      {cards.map((card, index) => (
        <MegaCard key={index} card={card} />
      ))}
    </div>
  );
}

/**
 * One department's mega panel, hung full-width from the bottom of the bar.
 *
 * Painted `fixed` for the reason the small panel is: two of the bars that
 * carry the category strip scroll sideways, and a box that scrolls on one axis
 * clips the other. `top` is the bar's bottom edge, measured by the caller.
 */
export function CategoryMegaPanel({
  id,
  branch,
  header,
  top,
  linkClassName,
}: {
  id: string;
  branch: ShelfBranch;
  header: StorefrontTheme['header'] | undefined;
  top: number;
  linkClassName?: string;
}) {
  const text = useShopContent();
  const categoryPath = useCategoryPath();
  const paths = useStorefrontPaths();
  const { category, children, grandchildren } = branch;
  const style = megaMenuStyle(header);
  const withPromo = style === 'columns_with_promo';

  const columns: MegaColumn[] = children.map((child) => ({
    key: child.id,
    heading: (
      <Link
        to={categoryPath(child.slug)}
        className={cn('mb-3 block text-base font-semibold hover:opacity-70', linkClassName)}
      >
        {child.name}
      </Link>
    ),
    links: (grandchildren[child.id] ?? []).map((leaf) => (
      <Link key={leaf.id} to={categoryPath(leaf.slug)} className="py-1 text-sm hover:opacity-70">
        {leaf.name}
      </Link>
    )),
  }));

  // The merchant's own link when they wrote one, the department otherwise.
  const promoTarget = megaTarget(
    header?.mega_menu_promo_href,
    { path: categoryPath(category.slug) },
    paths,
  );

  // The cards' defaults: the department, then its newest - pictured by its
  // sub-departments, then the department itself, then whatever the shop has.
  const pictures = useMegaPictures(
    [...children, category].map((each) =>
      each.image_url ? { imageUrl: each.image_url, imageFocal: each.image_focal } : null,
    ),
    style === 'columns_with_cards',
  );
  const cards = useMegaCards(header, [
    {
      to: { path: categoryPath(category.slug) },
      imageUrl: pictures[0]?.imageUrl ?? null,
      imageFocal: pictures[0]?.imageFocal,
    },
    {
      to: { path: newestFirst(categoryPath(category.slug)) },
      imageUrl: pictures[1]?.imageUrl ?? null,
      imageFocal: pictures[1]?.imageFocal,
    },
  ]);

  return (
    <MegaPanel id={id} label={category.name} header={header} top={top}>
      <MegaColumns columns={columns} className="flex-1" linksClassName="gap-1" />
      {style === 'columns_with_cards' ? <MegaCards cards={cards} /> : null}
      {withPromo ? (
        <MegaPromoTile
          heading={header?.mega_menu_promo_heading || text('theme.shop_all', { name: category.name })}
          to={promoTarget}
          imageUrl={header?.mega_menu_promo_image_url || category.image_url}
          imageFocal={header?.mega_menu_promo_image_url ? null : category.image_focal}
        />
      ) : null}
    </MegaPanel>
  );
}
